/**
 * Batch commands — the brew log's write path (PLAN §3 V1).
 *
 * Grouped, and every group follows a shape already established elsewhere in
 * this package so there is nothing new to learn:
 *
 * | Group | Commands | Shape they follow |
 * | --- | --- | --- |
 * | Lifecycle | `create` · `restore` · `resnapshot` · `delete` · `rename` · `setStatus` · `setBrewedAt` · `setPackagedOn` | `recipe.*` lifecycle |
 * | Log | `updateMeasurements` · `addFermentationReading` · `removeFermentationReading` · `setStageNotes` | `update…` / `add…` / `remove…` |
 * | Brew day | `toggleStep` · `setStepTimer` | keyed-map setters |
 * | Cellar | `addCellarTask` · `updateCellarTask` · `removeCellarTask` | list add/update/remove |
 * | Yeast | `setPitchedYeast` | `setInventoryDeducted` — a whole value or `null` |
 * | Yeast | `setYeastStarter` · `setYeastPitchedAt` | one-key patches of that record |
 * | Tasting | `addTasting` · `updateTasting` · `removeTasting` | list add/update/remove |
 * | Photos | `addPhoto` · `setPhotoCaption` · `removePhoto` | list add/update/remove |
 *
 * Three deliberate departures, the first two documented at their payload schema:
 *
 * 1. **`batch.create` requires its `from` document.** There is no blank batch —
 *    a batch *is* its two frozen snapshots, and this package can neither read
 *    `state.recipes` on the caller's behalf nor format a date into a name.
 * 2. **`batch.updateMeasurements` treats a present `null` as "clear".** Every
 *    other `changes`-shaped payload here skips nulls; a measurement is the one
 *    field family where "no reading" is a value a brewer chooses. See
 *    `mergePresent` in `../helpers.ts`.
 * 3. **`batch.resnapshot` re-takes a freeze the rest of this file treats as
 *    permanent.** It is the one write that touches `recipeSnapshot`, and it is
 *    refused on any batch that has become a record. See `batchResnapshotRefusal`
 *    in `document/batch.ts` for the four refusals and the reasoning.
 *
 * `batch.toggleStep` and `batch.setStepTimer` write into `Record<stepId, …>`
 * maps keyed by ids the *timeline builder* generates from the frozen snapshot.
 * That is the second reason the snapshot must be frozen: a timeline regenerated
 * from an edited recipe would produce different ids and orphan every tick — and
 * the reason `batch.resnapshot` refuses the moment one tick exists.
 */

import { PRIMING_SUGAR_LABELS } from '@beerwright/core';
import type { BatchDoc, BatchMeasurements, BatchResnapshotRefusal } from '../../document/batch';
import { BATCH_MAX_PHOTOS, batchResnapshotRefusal } from '../../document/batch';
import type { PackagingMethod } from '../../document/common';
import type { WorkspaceState } from '../../document/workspace';
import { BATCH_SCHEMA_VERSION } from '../../version';
import { defineCommand, inverseCommand } from '../definition';
import { commandError } from '../envelope';
import { quoted, transition } from '../format';
import {
  dropBatch,
  indexOfId,
  insertAt,
  mergeDefined,
  mergePresent,
  priorPresentValues,
  putBatch,
  removeAt,
  withBatch,
} from '../helpers';
import * as p from '../payloads';

/** Look a batch up for a summary, tolerating its absence (summaries never throw). */
function batchName(state: WorkspaceState, batchId: string): string {
  return state.batches[batchId]?.name ?? batchId;
}

/**
 * The culture a batch has chosen, for a summary. Falls back the way
 * {@link batchName} does: a summary is written before the command is applied
 * and must never throw, and the commands that read this refuse anyway when
 * there is no record to name.
 */
function pitchedCultureName(state: WorkspaceState, batchId: string): string {
  return state.batches[batchId]?.pitchedYeast?.name ?? batchId;
}

/** A gravity as a summary reads it: four decimals, the way a hydrometer is read. */
function gravity(sg: number): string {
  return sg.toFixed(4);
}

/**
 * A copy of a keyed map with one key set, or removed when the value is `null`.
 *
 * Removing rather than storing `null` keeps `completedSteps` and `timers` honest
 * as sets: "present ⇒ done" needs no second check, the JSON stays small on a
 * brew day with sixty steps, and `Object.keys().length` is the progress count.
 */
function withMapKey(
  map: Readonly<Record<string, number>>,
  key: string,
  value: number | null,
): Record<string, number> {
  const next = { ...map };
  if (value === null) delete next[key];
  else next[key] = value;
  return next;
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/**
 * Create a batch from a fully-built document.
 *
 * The document arrives whole — snapshots included — because building it needs
 * three things this package does not have: the workspace lookup that resolves a
 * recipe's equipment profile, a clock, and a locale to format "15 Aug 2026"
 * with. `buildBatchDoc` in `document/batch.ts` is the helper the caller uses;
 * this command's only jobs are to reject a duplicate id and to re-stamp
 * identity and time from the envelope, exactly as `recipe.create({ from })`
 * does for a duplicate.
 */
export const batchCreate = defineCommand({
  type: 'batch.create',
  payload: p.batchCreatePayloadSchema,
  apply: (state, command) => {
    const { batchId, from, name } = command.payload;
    if (state.batches[batchId] !== undefined) {
      return {
        ok: false,
        error: commandError('conflict', `A batch with id "${batchId}" already exists.`, {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    const doc: BatchDoc = {
      ...from,
      schemaVersion: BATCH_SCHEMA_VERSION,
      id: batchId,
      name: name ?? from.name,
      createdAt: command.timestamp,
      updatedAt: command.timestamp,
    };
    return { ok: true, state: putBatch(state, doc) };
  },
  invert: (_state, command) =>
    inverseCommand(
      command,
      'batch.delete',
      { batchId: command.payload.batchId },
      command.timestamp,
    ),
  summarize: (_state, command) =>
    `Log batch ${quoted(command.payload.name ?? command.payload.from.name)}`,
});

/**
 * Insert a batch verbatim, timestamps included — an **upsert**.
 *
 * The same triple duty `recipe.restore` carries: undo of `batch.delete`, the
 * exact inverse wherever a precise one would be lossy, and the whole-document
 * delta a full-state sync push uses when a client is too far behind for
 * incremental catch-up (PLAN §8.3).
 */
export const batchRestore = defineCommand({
  type: 'batch.restore',
  payload: p.batchRestorePayloadSchema,
  apply: (state, command) => ({ ok: true, state: putBatch(state, command.payload.batch) }),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batch.id];
    return prior === undefined
      ? inverseCommand(
          command,
          'batch.delete',
          { batchId: command.payload.batch.id },
          command.timestamp,
        )
      : inverseCommand(command, 'batch.restore', { batch: prior }, prior.updatedAt);
  },
  summarize: (_state, command) => `Restore batch ${quoted(command.payload.batch.name)}`,
});

/** What each refusal says to the brewer who pressed the button anyway. */
const RESNAPSHOT_REFUSALS: Readonly<Record<BatchResnapshotRefusal, string>> = {
  brewed:
    "This batch is already fermenting or later. Its copy of the recipe shows what you actually brewed, so we can't update it.",
  'brew-day-started':
    'Brew day has started on this batch. Re-taking the recipe now would renumber the steps and lose every tick and timer.',
  'inventory-deducted':
    'This batch has already been taken out of inventory against its current grist. Undo the deduction first, then re-take the recipe.',
  'recipe-gone': 'There is no live recipe to re-take this batch from.',
};

/**
 * Re-take a **planned** batch's two frozen copies from the live documents.
 *
 * The gap this closes: a brewer plans Saturday's brew on Tuesday, tweaks the
 * recipe on Friday, and — before this — brewed Tuesday's beer on Saturday with
 * nothing anywhere saying the copy had gone stale. The freeze is right for a
 * record and wrong for a plan, and `status: 'planned'` is the document already
 * telling us which one it is holding.
 *
 * Everything the batch has accumulated in its own right — name, stage notes,
 * cellar tasks, measurements, tastings, photographs — is kept. Only the two
 * snapshots and the `updatedAt` move, which is what makes this worth having
 * over "delete it and make a new one".
 *
 * The guard lives in `batchResnapshotRefusal` so the button that offers this
 * and the reducer that performs it cannot disagree about when it is allowed.
 * Invertible through `batch.restore`, which carries the prior document whole —
 * the same lossless inverse `batch.delete` uses, and the reason undo puts the
 * old recipe copy back exactly.
 */
export const batchResnapshot = defineCommand({
  type: 'batch.resnapshot',
  payload: p.batchResnapshotPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => {
      const refusal = batchResnapshotRefusal(batch, command.payload.recipe);
      if (refusal !== null) return commandError('invalid-state', RESNAPSHOT_REFUSALS[refusal]);
      return {
        ...batch,
        recipeSnapshot: { ...command.payload.recipe },
        equipmentSnapshot: { ...command.payload.equipment },
      };
    }),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    return prior === undefined
      ? null
      : inverseCommand(command, 'batch.restore', { batch: prior }, prior.updatedAt);
  },
  summarize: (state, command) =>
    `Update batch ${quoted(batchName(state, command.payload.batchId))} to the current recipe`,
});

/** Delete a batch. Invertible: the inverse carries the whole prior document. */
export const batchDelete = defineCommand({
  type: 'batch.delete',
  payload: p.batchDeletePayloadSchema,
  apply: (state, command) => {
    if (state.batches[command.payload.batchId] === undefined) {
      return {
        ok: false,
        error: commandError('not-found', `No batch with id "${command.payload.batchId}".`, {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    return { ok: true, state: dropBatch(state, command.payload.batchId) };
  },
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    return prior === undefined
      ? null
      : inverseCommand(command, 'batch.restore', { batch: prior }, prior.updatedAt);
  },
  summarize: (state, command) =>
    `Delete batch ${quoted(batchName(state, command.payload.batchId))}`,
});

/** Rename a batch. */
export const batchRename = defineCommand({
  type: 'batch.rename',
  payload: p.batchRenamePayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => ({
      ...batch,
      name: command.payload.name,
    })),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'batch.rename',
          { batchId: prior.id, name: prior.name },
          prior.updatedAt,
        );
  },
  summarize: (state, command) =>
    `Rename batch ${transition(
      quoted(batchName(state, command.payload.batchId)),
      quoted(command.payload.name),
    )}`,
});

/** Move a batch to another status. Any status to any status — see the payload's TSDoc. */
export const batchSetStatus = defineCommand({
  type: 'batch.setStatus',
  payload: p.batchSetStatusPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => ({
      ...batch,
      status: command.payload.status,
      packagedOn:
        command.payload.packagedOn === undefined ? batch.packagedOn : command.payload.packagedOn,
    })),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    if (prior === undefined) return null;
    // A move that wrote a packaged day puts the old one back with the old
    // status, so one undo restores both.
    const payload =
      command.payload.packagedOn === undefined
        ? { batchId: prior.id, status: prior.status }
        : { batchId: prior.id, status: prior.status, packagedOn: prior.packagedOn };
    return inverseCommand(command, 'batch.setStatus', payload, prior.updatedAt);
  },
  summarize: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    return prior === undefined
      ? `Set batch status to ${command.payload.status}`
      : `Move ${quoted(prior.name)} ${transition(prior.status, command.payload.status)}`;
  },
});

/** Set (or clear) the instant the brew day happened. */
export const batchSetBrewedAt = defineCommand({
  type: 'batch.setBrewedAt',
  payload: p.batchSetBrewedAtPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => ({
      ...batch,
      brewedAt: command.payload.brewedAt,
    })),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'batch.setBrewedAt',
          { batchId: prior.id, brewedAt: prior.brewedAt },
          prior.updatedAt,
        );
  },
  summarize: (state, command) => {
    const name = quoted(batchName(state, command.payload.batchId));
    // No date formatting: summaries are locale-free and deterministic (see
    // `format.ts`). Rendering the instant is the UI's job.
    return command.payload.brewedAt === null
      ? `Clear the brew date on ${name}`
      : `Set the brew date on ${name}`;
  },
});

/** Set (or clear) the day the batch was packaged. */
export const batchSetPackagedOn = defineCommand({
  type: 'batch.setPackagedOn',
  payload: p.batchSetPackagedOnPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => ({
      ...batch,
      packagedOn: command.payload.packagedOn,
    })),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'batch.setPackagedOn',
          { batchId: prior.id, packagedOn: prior.packagedOn },
          prior.updatedAt,
        );
  },
  summarize: (state, command) => {
    const name = quoted(batchName(state, command.payload.batchId));
    return command.payload.packagedOn === null
      ? `Clear the packaged date on ${name}`
      : `Set the packaged date on ${name}`;
  },
});

/**
 * Set (or clear) the instant this batch's ingredients left the cupboard.
 *
 * The idempotence guard for deduct-on-brew. It is a plain setter — a *fact*
 * about the batch — and deliberately not a "deduct" command: the deduction
 * itself is a list of `inventory.adjustQuantity` commands built by
 * `planInventoryDeduction`, and this one rides in the same `dispatchBatch` so
 * the whole operation is one undo step. Setting it is what makes the *next*
 * plan empty, which is where double deduction is prevented.
 */
export const batchSetInventoryDeducted = defineCommand({
  type: 'batch.setInventoryDeducted',
  payload: p.batchSetInventoryDeductedPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => ({
      ...batch,
      inventoryDeductedAt: command.payload.at,
      costSnapshot: command.payload.at === null ? null : (command.payload.costSnapshot ?? null),
    })),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'batch.setInventoryDeducted',
          {
            batchId: prior.id,
            at: prior.inventoryDeductedAt,
            costSnapshot: prior.costSnapshot,
          },
          prior.updatedAt,
        );
  },
  summarize: (state, command) => {
    const name = quoted(batchName(state, command.payload.batchId));
    return command.payload.at === null
      ? `Mark ${name} as not deducted from inventory`
      : `Mark ${name} as deducted from inventory`;
  },
});

/**
 * Record (or clear) the culture this batch is using.
 *
 * The half of the repitch lineage that lives on the batch, and now the moment
 * the jar leaves the fridge rather than only the moment it reaches the wort: a
 * record whose `pitchedAt` is null is a jar taken for a starter, and
 * `batch.setYeastPitchedAt` below is what finishes it. A later harvest asks
 * this field what generation went in — see `nextHarvestGeneration` in
 * `../../inventory/harvest.ts`.
 *
 * Whole-value, not a patch, and inverted with the prior record, so it undoes
 * the way `batch.setInventoryDeducted` does. The caller pairs it with the
 * `inventory.adjustQuantity` that takes the jar off the shelf; dispatching both
 * together is what makes an undone take put the yeast back.
 */
export const batchSetPitchedYeast = defineCommand({
  type: 'batch.setPitchedYeast',
  payload: p.batchSetPitchedYeastPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => ({
      ...batch,
      pitchedYeast: command.payload.pitchedYeast,
    })),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'batch.setPitchedYeast',
          { batchId: prior.id, pitchedYeast: prior.pitchedYeast },
          prior.updatedAt,
        );
  },
  summarize: (state, command) => {
    const name = quoted(batchName(state, command.payload.batchId));
    const pitched = command.payload.pitchedYeast;
    if (pitched === null) return `Clear the yeast pitched into ${name}`;
    const culture = `${quoted(pitched.name)}, gen ${pitched.generation}`;
    // A record with no pitch instant is a jar taken out of the fridge for a
    // starter, which is days short of a pitch and must not claim to be one.
    return pitched.pitchedAt === null
      ? `Take ${culture}, for ${name}`
      : `Pitch ${culture}, into ${name}`;
  },
});

/**
 * Size (or clear) the starter this batch is building from the jar it took.
 *
 * A patch, unlike `batch.setPitchedYeast` next door, because everything else on
 * the record is the jar's provenance: a resize is not an occasion to restate
 * which lot was taken or what generation it is on. The prior starter is what it
 * inverts with, so a resize undoes to the size before it rather than to none.
 *
 * Two refusals. A starter with no culture under it would be sized against an
 * imaginary pack, which is the mistake moving the starter onto the batch exists
 * to end. A starter on dry yeast is refused because core's planner refuses one
 * and says why, and the document should not hold a plan the engine will ignore.
 */
/** How a summary says a packaging method, as a verb on the batch. */
const PACKAGED_AS: Readonly<Record<PackagingMethod, string>> = {
  bottle: 'bottled',
  keg: 'kegged',
  'keg-conditioning': 'keg-conditioned',
};

/**
 * Change how this batch is packaged, never the recipe (PLAN-BATCH-PACKAGING
 * D-BP2). Present keys are written, `null` included, because `null` is the
 * brewer handing a choice back to the plan; the inverse restates exactly the
 * keys the command named. Allowed at any status: a packaged batch's record is
 * only as good as its last correction.
 */
export const batchSetPackaging = defineCommand({
  type: 'batch.setPackaging',
  payload: p.batchSetPackagingPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => {
      const { batchId: _batchId, ...changes } = command.payload;
      return { ...batch, packaging: mergePresent(batch.packaging, changes) };
    }),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    if (prior === undefined) return null;
    const { batchId: _batchId, ...changes } = command.payload;
    return inverseCommand(
      command,
      'batch.setPackaging',
      { batchId: prior.id, ...priorPresentValues(prior.packaging, changes) },
      prior.updatedAt,
    );
  },
  summarize: (state, command) => {
    const batch = quoted(batchName(state, command.payload.batchId));
    const { method, primingSugar, carbonationVolumes } = command.payload;
    const parts: string[] = [];
    if (method !== undefined) {
      parts.push(
        method === null
          ? `${batch} is packaged as the recipe planned`
          : `Package ${batch} ${PACKAGED_AS[method]}`,
      );
    }
    if (primingSugar !== undefined) {
      parts.push(
        primingSugar === null
          ? `${batch} is primed as the recipe planned`
          : `Prime ${batch} with ${PRIMING_SUGAR_LABELS[primingSugar].toLowerCase()}`,
      );
    }
    if (carbonationVolumes !== undefined) {
      parts.push(
        carbonationVolumes === null
          ? `${batch} carbonates to the recipe's target`
          : `Carbonate ${batch} to ${carbonationVolumes.toFixed(1)} vol`,
      );
    }
    return parts.join('; ');
  },
});

export const batchSetYeastStarter = defineCommand({
  type: 'batch.setYeastStarter',
  payload: p.batchSetYeastStarterPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => {
      const record = batch.pitchedYeast;
      if (record === null) {
        return commandError(
          'conflict',
          'Choose a culture for this batch before sizing its starter.',
        );
      }
      if (record.form === 'dry') {
        return commandError('conflict', "Dry yeast doesn't get a starter.");
      }
      return { ...batch, pitchedYeast: { ...record, starter: command.payload.starter } };
    }),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    if (prior === undefined || prior.pitchedYeast === null) return null;
    return inverseCommand(
      command,
      'batch.setYeastStarter',
      { batchId: prior.id, starter: prior.pitchedYeast.starter },
      prior.updatedAt,
    );
  },
  summarize: (state, command) => {
    const batch = quoted(batchName(state, command.payload.batchId));
    const culture = quoted(pitchedCultureName(state, command.payload.batchId));
    return command.payload.starter === null
      ? `Take the starter off ${culture} on ${batch}`
      : `Size a starter for ${culture} on ${batch}`;
  },
});

/**
 * Stamp (or unstamp) the moment the chosen culture reached the wort.
 *
 * The take and the pitch are days apart whenever a starter is involved, so they
 * are two writes. This one touches nothing but `pitchedAt`, which is what keeps
 * "taken on Tuesday, pitched on Saturday" recordable without the Saturday press
 * restating Tuesday's jar.
 */
export const batchSetYeastPitchedAt = defineCommand({
  type: 'batch.setYeastPitchedAt',
  payload: p.batchSetYeastPitchedAtPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => {
      const record = batch.pitchedYeast;
      if (record === null) {
        return commandError(
          'conflict',
          'Choose a culture for this batch before recording the pitch.',
        );
      }
      return { ...batch, pitchedYeast: { ...record, pitchedAt: command.payload.pitchedAt } };
    }),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    if (prior === undefined || prior.pitchedYeast === null) return null;
    return inverseCommand(
      command,
      'batch.setYeastPitchedAt',
      { batchId: prior.id, pitchedAt: prior.pitchedYeast.pitchedAt },
      prior.updatedAt,
    );
  },
  summarize: (state, command) => {
    const batch = quoted(batchName(state, command.payload.batchId));
    const culture = quoted(pitchedCultureName(state, command.payload.batchId));
    return command.payload.pitchedAt === null
      ? `Remove ${culture} from ${batch}`
      : `Pitch ${culture} into ${batch}`;
  },
});

// ---------------------------------------------------------------------------
// The log
// ---------------------------------------------------------------------------

/**
 * Merge measurements onto a batch.
 *
 * Present-key semantics, `null` included — see
 * {@link p.batchMeasurementChangesSchema}. `preBoil`/`postBoil` merge one level
 * deep, which is what lets the brew-day sheet capture a refractometer reading
 * now and a dipstick volume five minutes later without either wiping the other.
 */
export const batchUpdateMeasurements = defineCommand({
  type: 'batch.updateMeasurements',
  payload: p.batchUpdateMeasurementsPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => {
      const { preBoil, postBoil, kettle, ...scalars } = command.payload.measurements;
      const next: BatchMeasurements = mergePresent(batch.measurements, scalars);
      return {
        ...batch,
        measurements: {
          ...next,
          ...(preBoil === undefined
            ? {}
            : { preBoil: mergePresent(batch.measurements.preBoil, preBoil) }),
          ...(postBoil === undefined
            ? {}
            : { postBoil: mergePresent(batch.measurements.postBoil, postBoil) }),
          ...(kettle === undefined
            ? {}
            : { kettle: mergePresent(batch.measurements.kettle, kettle) }),
        },
      };
    }),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    if (prior === undefined) return null;
    const { preBoil, postBoil, kettle, ...scalars } = command.payload.measurements;
    return inverseCommand(
      command,
      'batch.updateMeasurements',
      {
        batchId: prior.id,
        measurements: {
          ...priorPresentValues(prior.measurements, scalars),
          ...(preBoil === undefined
            ? {}
            : { preBoil: priorPresentValues(prior.measurements.preBoil, preBoil) }),
          ...(postBoil === undefined
            ? {}
            : { postBoil: priorPresentValues(prior.measurements.postBoil, postBoil) }),
          ...(kettle === undefined
            ? {}
            : { kettle: priorPresentValues(prior.measurements.kettle, kettle) }),
        },
      },
      prior.updatedAt,
    );
  },
  summarize: (_state, command) => {
    const { preBoil, postBoil, originalGravity, finalGravity } = command.payload.measurements;
    if (originalGravity !== undefined && originalGravity !== null) {
      return `Log OG ${gravity(originalGravity)}`;
    }
    if (finalGravity !== undefined && finalGravity !== null) {
      return `Log FG ${gravity(finalGravity)}`;
    }
    if (preBoil?.sg !== undefined && preBoil.sg !== null) {
      return `Log pre-boil gravity ${gravity(preBoil.sg)}`;
    }
    if (postBoil?.sg !== undefined && postBoil.sg !== null) {
      return `Log post-boil gravity ${gravity(postBoil.sg)}`;
    }
    const kettle = command.payload.measurements.kettle;
    if (kettle?.preBoilPh !== undefined && kettle.preBoilPh !== null) {
      return `Log pre-boil pH ${kettle.preBoilPh.toFixed(2)}`;
    }
    if (kettle?.afterAdjustmentPh !== undefined && kettle.afterAdjustmentPh !== null) {
      return `Log kettle pH after adjustment ${kettle.afterAdjustmentPh.toFixed(2)}`;
    }
    const keys = Object.keys(command.payload.measurements).sort().join(', ');
    return keys === '' ? 'Update measurements' : `Update measurements (${keys})`;
  },
});

/** Append (or insert) a fermentation gravity reading. */
export const batchAddFermentationReading = defineCommand({
  type: 'batch.addFermentationReading',
  payload: p.batchAddFermentationReadingPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => {
      const { reading, index } = command.payload;
      if (
        indexOfId(batch.measurements.fermentationReadings, 'readingId', reading.readingId) !== -1
      ) {
        return commandError(
          'conflict',
          `A fermentation reading with id "${reading.readingId}" is already on this batch.`,
        );
      }
      return {
        ...batch,
        measurements: {
          ...batch.measurements,
          fermentationReadings: insertAt(batch.measurements.fermentationReadings, reading, index),
        },
      };
    }),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'batch.removeFermentationReading',
          { batchId: prior.id, readingId: command.payload.reading.readingId },
          prior.updatedAt,
        );
  },
  summarize: (_state, command) => `Log fermentation gravity ${gravity(command.payload.reading.sg)}`,
});

/** Remove one fermentation gravity reading. Invertible with its original position. */
export const batchRemoveFermentationReading = defineCommand({
  type: 'batch.removeFermentationReading',
  payload: p.batchRemoveFermentationReadingPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => {
      const index = indexOfId(
        batch.measurements.fermentationReadings,
        'readingId',
        command.payload.readingId,
      );
      if (index === -1) {
        return commandError(
          'not-found',
          `No fermentation reading with id "${command.payload.readingId}" on this batch.`,
        );
      }
      return {
        ...batch,
        measurements: {
          ...batch.measurements,
          fermentationReadings: removeAt(batch.measurements.fermentationReadings, index),
        },
      };
    }),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    const index =
      prior === undefined
        ? -1
        : indexOfId(
            prior.measurements.fermentationReadings,
            'readingId',
            command.payload.readingId,
          );
    const reading = prior?.measurements.fermentationReadings[index];
    return prior === undefined || reading === undefined
      ? null
      : inverseCommand(
          command,
          'batch.addFermentationReading',
          { batchId: prior.id, reading, index },
          prior.updatedAt,
        );
  },
  summarize: (state, command) => {
    const reading = state.batches[command.payload.batchId]?.measurements.fermentationReadings.find(
      (r) => r.readingId === command.payload.readingId,
    );
    return reading === undefined
      ? 'Remove a fermentation reading'
      : `Remove fermentation reading ${gravity(reading.sg)}`;
  },
});

/** Replace the prose for one stage. */
export const batchSetStageNotes = defineCommand({
  type: 'batch.setStageNotes',
  payload: p.batchSetStageNotesPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => ({
      ...batch,
      stageNotes: { ...batch.stageNotes, [command.payload.stage]: command.payload.notes },
    })),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'batch.setStageNotes',
          {
            batchId: prior.id,
            stage: command.payload.stage,
            notes: prior.stageNotes[command.payload.stage],
          },
          prior.updatedAt,
        );
  },
  summarize: (_state, command) =>
    command.payload.notes.length === 0
      ? `Clear the ${command.payload.stage} notes`
      : `Update the ${command.payload.stage} notes`,
});

// ---------------------------------------------------------------------------
// Brew day
// ---------------------------------------------------------------------------

/**
 * Tick or un-tick one brew-day step.
 *
 * The value stored is the completion *instant*, not `true`. Two devices on the
 * same brew day (the tablet in the kitchen, the phone in the garage) therefore
 * converge on a number under the sync engine's field-granularity
 * last-write-wins, and the finished brew sheet can print the time each step was
 * actually done.
 */
export const batchToggleStep = defineCommand({
  type: 'batch.toggleStep',
  payload: p.batchToggleStepPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => ({
      ...batch,
      completedSteps: withMapKey(batch.completedSteps, command.payload.stepId, command.payload.at),
    })),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'batch.toggleStep',
          {
            batchId: prior.id,
            stepId: command.payload.stepId,
            at: prior.completedSteps[command.payload.stepId] ?? null,
          },
          prior.updatedAt,
        );
  },
  summarize: (_state, command) =>
    command.payload.at === null
      ? `Un-tick step ${quoted(command.payload.stepId)}`
      : `Tick step ${quoted(command.payload.stepId)}`,
});

/**
 * Start, restart or clear one step's timer.
 *
 * Stores a started-at anchor and never a remaining duration. See the payload's
 * TSDoc, and `document/batch.ts`'s header for why that is the only
 * representation that survives a reload, a sleeping tablet and a sync.
 */
export const batchSetStepTimer = defineCommand({
  type: 'batch.setStepTimer',
  payload: p.batchSetStepTimerPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => ({
      ...batch,
      timers: withMapKey(batch.timers, command.payload.stepId, command.payload.startedAt),
    })),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'batch.setStepTimer',
          {
            batchId: prior.id,
            stepId: command.payload.stepId,
            startedAt: prior.timers[command.payload.stepId] ?? null,
          },
          prior.updatedAt,
        );
  },
  summarize: (_state, command) =>
    command.payload.startedAt === null
      ? `Clear the timer on step ${quoted(command.payload.stepId)}`
      : `Start the timer on step ${quoted(command.payload.stepId)}`,
});

// ---------------------------------------------------------------------------
// Cellar
// ---------------------------------------------------------------------------

/** Append (or insert) a cellar task. */
export const batchAddCellarTask = defineCommand({
  type: 'batch.addCellarTask',
  payload: p.batchAddCellarTaskPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => {
      const { task, index } = command.payload;
      if (indexOfId(batch.cellarTasks, 'taskId', task.taskId) !== -1) {
        return commandError(
          'conflict',
          `A cellar task with id "${task.taskId}" is already on this batch.`,
        );
      }
      return { ...batch, cellarTasks: insertAt(batch.cellarTasks, task, index) };
    }),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'batch.removeCellarTask',
          { batchId: prior.id, taskId: command.payload.task.taskId },
          prior.updatedAt,
        );
  },
  summarize: (state, command) =>
    `Schedule ${quoted(command.payload.task.title)} on ${quoted(batchName(state, command.payload.batchId))}`,
});

/** Patch one cellar task. */
export const batchUpdateCellarTask = defineCommand({
  type: 'batch.updateCellarTask',
  payload: p.batchUpdateCellarTaskPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => {
      const index = indexOfId(batch.cellarTasks, 'taskId', command.payload.taskId);
      const current = batch.cellarTasks[index];
      if (current === undefined) {
        return commandError(
          'not-found',
          `No cellar task with id "${command.payload.taskId}" on this batch.`,
        );
      }
      const next = [...batch.cellarTasks];
      next[index] = mergeDefined(current, command.payload.changes);
      return { ...batch, cellarTasks: next };
    }),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    const current =
      prior === undefined
        ? undefined
        : prior.cellarTasks[indexOfId(prior.cellarTasks, 'taskId', command.payload.taskId)];
    if (prior === undefined || current === undefined) return null;
    const changes: typeof command.payload.changes = {};
    for (const key of Object.keys(
      command.payload.changes,
    ) as (keyof typeof command.payload.changes)[]) {
      if (command.payload.changes[key] !== undefined) {
        (changes as Record<string, unknown>)[key] = current[key];
      }
    }
    return inverseCommand(
      command,
      'batch.updateCellarTask',
      { batchId: prior.id, taskId: command.payload.taskId, changes },
      prior.updatedAt,
    );
  },
  summarize: (_state, command) =>
    command.payload.changes.doneAt !== undefined
      ? command.payload.changes.doneAt === null
        ? 'Reopen a cellar task'
        : 'Mark a cellar task done'
      : 'Update a cellar task',
});

/** Remove one cellar task. Invertible with its original position. */
export const batchRemoveCellarTask = defineCommand({
  type: 'batch.removeCellarTask',
  payload: p.batchRemoveCellarTaskPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => {
      const index = indexOfId(batch.cellarTasks, 'taskId', command.payload.taskId);
      if (index === -1) {
        return commandError(
          'not-found',
          `No cellar task with id "${command.payload.taskId}" on this batch.`,
        );
      }
      return { ...batch, cellarTasks: removeAt(batch.cellarTasks, index) };
    }),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    const index =
      prior === undefined ? -1 : indexOfId(prior.cellarTasks, 'taskId', command.payload.taskId);
    const task = prior?.cellarTasks[index];
    return prior === undefined || task === undefined
      ? null
      : inverseCommand(
          command,
          'batch.addCellarTask',
          { batchId: prior.id, task, index },
          prior.updatedAt,
        );
  },
  summarize: (state, command) => {
    const task = state.batches[command.payload.batchId]?.cellarTasks.find(
      (item) => item.taskId === command.payload.taskId,
    );
    return task === undefined ? 'Remove a cellar task' : `Remove ${quoted(task.title)}`;
  },
});

// ---------------------------------------------------------------------------
// Tasting
// ---------------------------------------------------------------------------

/**
 * Add, patch and remove a tasting note (PLAN-COMPETITIVE C2).
 *
 * The cellar-task list shape, with one departure: **`updateTasting` merges by
 * presence rather than by definedness**, so a `null` sent for a section clears
 * it. That is `batch.updateMeasurements`' convention rather than
 * `batch.updateCellarTask`'s, and it is here for the same reason it is there —
 * an unscored section is a value a brewer chooses, distinct from a section this
 * patch simply did not mention. The inverse is built with `priorPresentValues`,
 * the counterpart that agrees with `mergePresent` on which keys were touched;
 * pairing it with the wrong one is how an undo stops being an identity.
 */

/** Append (or insert) a tasting note. */
export const batchAddTasting = defineCommand({
  type: 'batch.addTasting',
  payload: p.batchAddTastingPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => {
      const { tasting, index } = command.payload;
      if (indexOfId(batch.tastings, 'tastingId', tasting.tastingId) !== -1) {
        return commandError(
          'conflict',
          `A tasting with id "${tasting.tastingId}" is already on this batch.`,
        );
      }
      return { ...batch, tastings: insertAt(batch.tastings, tasting, index) };
    }),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'batch.removeTasting',
          { batchId: prior.id, tastingId: command.payload.tasting.tastingId },
          prior.updatedAt,
        );
  },
  summarize: (state, command) =>
    `Add a tasting note to ${quoted(batchName(state, command.payload.batchId))}`,
});

/** Patch one tasting. A present `null` clears a section. */
export const batchUpdateTasting = defineCommand({
  type: 'batch.updateTasting',
  payload: p.batchUpdateTastingPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => {
      const index = indexOfId(batch.tastings, 'tastingId', command.payload.tastingId);
      const current = batch.tastings[index];
      if (current === undefined) {
        return commandError(
          'not-found',
          `No tasting with id "${command.payload.tastingId}" on this batch.`,
        );
      }
      const next = [...batch.tastings];
      next[index] = mergePresent(current, command.payload.changes);
      return { ...batch, tastings: next };
    }),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    const current =
      prior === undefined
        ? undefined
        : prior.tastings[indexOfId(prior.tastings, 'tastingId', command.payload.tastingId)];
    if (prior === undefined || current === undefined) return null;
    return inverseCommand(
      command,
      'batch.updateTasting',
      {
        batchId: prior.id,
        tastingId: command.payload.tastingId,
        changes: priorPresentValues(current, command.payload.changes),
      },
      prior.updatedAt,
    );
  },
  summarize: (_state, command) =>
    command.payload.changes.notes !== undefined && Object.keys(command.payload.changes).length === 1
      ? 'Rewrite a tasting note'
      : 'Update a tasting note',
});

/** Remove one tasting. Invertible with its original position. */
export const batchRemoveTasting = defineCommand({
  type: 'batch.removeTasting',
  payload: p.batchRemoveTastingPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => {
      const index = indexOfId(batch.tastings, 'tastingId', command.payload.tastingId);
      if (index === -1) {
        return commandError(
          'not-found',
          `No tasting with id "${command.payload.tastingId}" on this batch.`,
        );
      }
      return { ...batch, tastings: removeAt(batch.tastings, index) };
    }),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    const index =
      prior === undefined ? -1 : indexOfId(prior.tastings, 'tastingId', command.payload.tastingId);
    const tasting = prior?.tastings[index];
    return prior === undefined || tasting === undefined
      ? null
      : inverseCommand(
          command,
          'batch.addTasting',
          { batchId: prior.id, tasting, index },
          prior.updatedAt,
        );
  },
  summarize: (state, command) =>
    `Remove a tasting note from ${quoted(batchName(state, command.payload.batchId))}`,
});

// ---------------------------------------------------------------------------
// Photographs (PLAN-COMPETITIVE C4)
// ---------------------------------------------------------------------------

/**
 * Record a photograph the Worker has already stored.
 *
 * The cap is enforced here rather than only by the `.max()` on the document's
 * array, because the two are different jobs: the array bound decides whether a
 * document that already exists is well formed, and this decides whether a
 * command is allowed to make one that is not. A refusal is an ordinary
 * `conflict`, and the upload route pre-checks the same number against the same
 * constant so the brewer is told before their bytes go up the wire rather than
 * after.
 */
export const batchAddPhoto = defineCommand({
  type: 'batch.addPhoto',
  payload: p.batchAddPhotoPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => {
      const { photo, index } = command.payload;
      if (indexOfId(batch.photos, 'photoId', photo.photoId) !== -1) {
        return commandError(
          'conflict',
          `A photo with id "${photo.photoId}" is already on this batch.`,
        );
      }
      if (batch.photos.length >= BATCH_MAX_PHOTOS) {
        return commandError(
          'conflict',
          `A batch holds at most ${BATCH_MAX_PHOTOS} photos. Remove one to add another.`,
        );
      }
      return { ...batch, photos: insertAt(batch.photos, photo, index) };
    }),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'batch.removePhoto',
          { batchId: prior.id, photoId: command.payload.photo.photoId },
          prior.updatedAt,
        );
  },
  summarize: (state, command) =>
    `Add a photo to ${quoted(batchName(state, command.payload.batchId))}`,
});

/** Write one photograph's caption. Invertible with the caption it replaced. */
export const batchSetPhotoCaption = defineCommand({
  type: 'batch.setPhotoCaption',
  payload: p.batchSetPhotoCaptionPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => {
      const index = indexOfId(batch.photos, 'photoId', command.payload.photoId);
      const current = batch.photos[index];
      if (current === undefined) {
        return commandError(
          'not-found',
          `No photo with id "${command.payload.photoId}" on this batch.`,
        );
      }
      const next = [...batch.photos];
      next[index] = { ...current, caption: command.payload.caption };
      return { ...batch, photos: next };
    }),
  invert: (state, command) => {
    const prior = state.batches[command.payload.batchId];
    const current = prior?.photos[indexOfId(prior.photos, 'photoId', command.payload.photoId)];
    return prior === undefined || current === undefined
      ? null
      : inverseCommand(
          command,
          'batch.setPhotoCaption',
          { batchId: prior.id, photoId: current.photoId, caption: current.caption },
          prior.updatedAt,
        );
  },
  summarize: (_state, command) =>
    command.payload.caption === '' ? 'Clear a photo caption' : 'Caption a photo',
});

/**
 * Take a photograph off a batch.
 *
 * **The one command in this package that is deliberately not invertible**, and
 * it is worth saying why rather than leaving the `null` to be read as an
 * oversight.
 *
 * Every other list removal here restores the item it took, because the item
 * *is* the data. A photo descriptor is not: the data is an R2 object, and the
 * route that removes a photo deletes that object, because the alternative is a
 * file nothing points at and nothing can ever find again. An inverse would
 * therefore put a tile back over bytes that are gone — an undo that looks like
 * it worked and did not, which is worse than one that is honestly unavailable.
 *
 * `workspace-store.ts` reads a `null` inverse as "this step does not go on the
 * undo stack", so ⌘Z after removing a photo undoes whatever the brewer did
 * before it, and the confirmation in the UI is what asks first.
 */
export const batchRemovePhoto = defineCommand({
  type: 'batch.removePhoto',
  payload: p.batchRemovePhotoPayloadSchema,
  apply: (state, command) =>
    withBatch(state, command, command.payload.batchId, (batch) => {
      const index = indexOfId(batch.photos, 'photoId', command.payload.photoId);
      if (index === -1) {
        return commandError(
          'not-found',
          `No photo with id "${command.payload.photoId}" on this batch.`,
        );
      }
      return { ...batch, photos: removeAt(batch.photos, index) };
    }),
  invert: () => null,
  summarize: () => 'Remove a photo',
});
